import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api-client';
import { StatCard } from '../../components/ui/stat-card';

interface StatItem {
  deposit: number;
  topup: number;
  withdraw: number;
  settle: number;
  myPayinShare: number;
  myPayoutShare: number;
  payinSharePercent: number;
  payoutSharePercent: number;
}

interface StatsResponse {
  items: StatItem[];
  lastStatSyncAt: string | null;
}

function getMonthOptions(): { value: string; label: string }[] {
  const opts: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    opts.push({ value, label });
  }
  return opts;
}

function pad(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getDateRangeForMonth(yearMonth: string): { dateFrom: string; dateTo: string } {
  const [year, month] = yearMonth.split('-').map(Number);
  const lastDay = new Date(year, month, 0);
  const now = new Date();
  const isCurrent = year === now.getFullYear() && month === now.getMonth() + 1;
  const endDate = isCurrent ? now : lastDay;
  return { dateFrom: `${yearMonth}-01`, dateTo: pad(endDate) };
}

function syncCutoff(yearMonth: string): Date {
  const [year, month] = yearMonth.split('-').map(Number);
  const now = new Date();
  const isCurrent = year === now.getFullYear() && month === now.getMonth() + 1;
  if (isCurrent) {
    const offset = 7 * 60 * 60 * 1000;
    const shifted = new Date(now.getTime() + offset);
    const startOfDayUtc = Date.UTC(
      shifted.getUTCFullYear(),
      shifted.getUTCMonth(),
      shifted.getUTCDate(),
    );
    return new Date(startOfDayUtc - offset);
  }
  return new Date(`${year}-${String(month + 1).padStart(2, '0')}-01T00:00:00.000+07:00`);
}

export function SubSellerDashboard() {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const monthOptions = getMonthOptions();
  const { dateFrom, dateTo } = getDateRangeForMonth(selectedMonth);

  const { data, isLoading } = useQuery({
    queryKey: ['sub-seller', 'stats', dateFrom, dateTo],
    queryFn: () =>
      apiGet<StatsResponse>('/sub-seller/stats', { dateFrom, dateTo, pageSize: '100' }),
  });

  const stats = data?.ok ? data.data.items : [];
  const lastSyncStr = data?.ok ? data.data.lastStatSyncAt : null;
  const lastSync = lastSyncStr ? new Date(lastSyncStr) : null;

  const totalDeposit = stats.reduce((sum, s) => sum + s.deposit, 0);
  const totalTopup = stats.reduce((sum, s) => sum + s.topup, 0);
  const totalWithdraw = stats.reduce((sum, s) => sum + s.withdraw, 0);
  const totalSettle = stats.reduce((sum, s) => sum + s.settle, 0);
  const totalPayinShare = stats.reduce((sum, s) => sum + s.myPayinShare, 0);
  const totalPayoutShare = stats.reduce((sum, s) => sum + s.myPayoutShare, 0);
  const payinPct = stats.length > 0 ? stats[0].payinSharePercent : 0;
  const payoutPct = stats.length > 0 ? stats[0].payoutSharePercent : 0;

  const formatNum = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  let syncStatus: 'never' | 'partial' | 'fresh' = 'fresh';
  if (!lastSync) {
    syncStatus = 'never';
  } else if (lastSync < syncCutoff(selectedMonth)) {
    syncStatus = 'partial';
  }
  const lastSyncLabel = lastSync
    ? lastSync.toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
        >
          {monthOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="text-xs text-gray-500 dark:text-gray-400">
        {dateFrom} ~ {dateTo}
      </div>

      {!isLoading && syncStatus === 'never' && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          <div className="font-medium">{t('dashboard.syncNever')}</div>
          <div className="mt-1 text-xs">{t('dashboard.syncNeverHintSub')}</div>
        </div>
      )}
      {!isLoading && syncStatus === 'partial' && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <div className="font-medium">{t('dashboard.syncPartial')}</div>
          <div className="mt-1 text-xs">
            {t('dashboard.lastSyncAt', { time: lastSyncLabel })} —{' '}
            {t('dashboard.syncPartialHintSub')}
          </div>
        </div>
      )}
      {!isLoading && syncStatus === 'fresh' && lastSyncLabel && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-xs text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300">
          {t('dashboard.syncFresh', { time: lastSyncLabel })}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t('dashboard.totalDeposits')}
          value={formatNum(totalDeposit)}
          icon="D"
          accentClass="bg-gradient-to-tr from-emerald-600 to-emerald-400"
        />
        <StatCard
          label={t('dashboard.totalTopups')}
          value={formatNum(totalTopup)}
          icon="T"
          accentClass="bg-gradient-primary"
        />
        <StatCard
          label={t('dashboard.totalWithdraws')}
          value={formatNum(totalWithdraw)}
          icon="W"
          accentClass="bg-gradient-to-tr from-orange-600 to-amber-400"
        />
        <StatCard
          label={t('dashboard.totalSettles')}
          value={formatNum(totalSettle)}
          icon="S"
          accentClass="bg-gradient-to-tr from-violet-600 to-fuchsia-500"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard
          label={`${t('transactions.calcPayinFee')} (${payinPct}%)`}
          value={formatNum(totalPayinShare)}
          hint={`(${t('transactions.deposit')} + ${t('transactions.topup')}) × ${payinPct}%`}
          icon="↘"
          accentClass="bg-gradient-primary"
        />
        <StatCard
          label={`${t('transactions.calcPayoutFee')} (${payoutPct}%)`}
          value={formatNum(totalPayoutShare)}
          hint={`(${t('transactions.withdraw')} + ${t('transactions.settle')}) × ${payoutPct}%`}
          icon="↗"
          accentClass="bg-gradient-to-tr from-emerald-600 to-emerald-400"
        />
      </div>
    </div>
  );
}
